import { notify } from '@/components/composed/notify';
import { ConfirmDialog } from '@/components/composed/ConfirmDialog';
import { ApiError } from '@/lib/api/errors';
import type { EntityCreateInput } from '@/lib/api/domains/entities';
import { useCreateEntity } from '../api/entity-mutations';

/*
 * Pre-commit confirmation surface (E02.F04 R21). Before an operator saves a new
 * organization, this names what saving it commits Kelly to — that Kelly will
 * SERVICE the organization effective the day it is created (AC-341cc0ea), and
 * that Kelly is the party ENTITLED TO END that servicing relationship
 * (AC-dd48c81d), both in the same place and before the create is committed.
 *
 * Abandoning (Cancel / dismiss) creates NEITHER the organization nor the
 * relationship (AC-ed17e8e1): the create mutation only runs on confirm. On
 * confirm, the server writes both or neither; the confirmation names BOTH the
 * organization and its servicing relationship (AC-c6d9e7e3). There is no third
 * path that creates the organization without the relationship — the only
 * confirming action runs the one create that commits both.
 *
 * The frontend is the courtesy half: it SHOWS the commitment but holds no
 * authorization; the server is the authority on who may create and on what the
 * create wrote. Pessimistic (mutate → invalidate), no optimistic update.
 */
export function CreateEntityConfirm({
  input,
  open,
  onOpenChange,
  onCreated,
}: {
  input: EntityCreateInput;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (servicingCreated: boolean) => void;
}) {
  const createEntity = useCreateEntity();

  async function onConfirm() {
    try {
      const result = await createEntity.mutateAsync(input);
      // Confirm back that BOTH now exist (AC-c6d9e7e3): the organization and —
      // for every kind but the Software record itself — its servicing
      // relationship.
      const servicing = Boolean(result.servicingRelationship);
      notify.success(
        servicing
          ? `${result.entity.legalName} created, and Kelly's servicing relationship with it now exists.`
          : `${result.entity.legalName} created.`,
      );
      onCreated?.(servicing);
    } catch (error) {
      notify.error(
        error instanceof ApiError
          ? error.message
          : 'Failed to create the organization.',
      );
      throw error;
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Create ${input.legalName}?`}
      description={
        <span>
          Saving this organization makes <strong>Kelly its servicer</strong>,
          effective today. <strong>Kelly</strong> is the party entitled to end
          that servicing relationship. The organization and its servicing
          relationship are created together — there is no way to create one
          without the other.
        </span>
      }
      confirmLabel="Create organization and servicing relationship"
      cancelLabel="Abandon"
      onConfirm={onConfirm}
    />
  );
}
